import * as React from 'react';
import { MouseEvent, useRef, useState } from 'react';
import { styled, Theme } from '../../theme';
import { css } from '@emotion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { setupTooltipProps, TooltipDefinition } from '../../utils/Tooltips';
import { useDimension } from '../../hooks/useDimension';

export type ButtonAction = (event: MouseEvent, loading: (loading: boolean) => any) => any;

export enum ButtonIconPosition {
  RIGHT = 'right',
  LEFT = 'left'
}

export enum ButtonType {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  TERTIARY = 'tertiary'
}

export interface Btn extends TooltipDefinition {
  label?: string;
  type?: ButtonType;
  action: ButtonAction;
  loadingOverride?: boolean;
  disabled?: boolean;
  icon?: string | JSX.Element;
  iconPosition?: ButtonIconPosition;
  className?: any;
}

export function useSubmit(props: Btn): { action: (event: MouseEvent) => any; blocking: boolean } {
  const [block, setBlocking] = useState(false);
  return {
    action: (event: MouseEvent) => {
      if (props.loadingOverride || block || props.disabled) {
        return;
      }
      props.action(event, (loading: boolean) => {
        setBlocking(loading);
      });
    },
    blocking: block
  };
}

export const ButtonWidget: React.FC<Btn & { forwardRef?: React.RefObject<HTMLElement> }> = (props) => {
  const buttonRef = props.forwardRef || useRef<HTMLElement>(null);

  const dim = useDimension({ ref: buttonRef });
  const [minWidth, setMinWidth] = useState<number | undefined>();

  const { action, blocking } = useSubmit({
    ...props,
    action: (event, loading) => {
      props.action(event, (isLoading) => {
        if (isLoading && buttonRef.current) {
          setMinWidth(dim?.width);
        }
        loading(isLoading);
      });
    }
  });

  const getContent = () => {
    if (blocking || props.loadingOverride) {
      return (
        <S.LoaderBox>
          <FontAwesomeIcon icon="spinner" spin={true} />
        </S.LoaderBox>
      );
    }
    const labelAsset = !!props.label && <S.Label>{props.label}</S.Label>;
    let asset = null;
    if (props.icon) {
      if (React.isValidElement(props.icon)) {
        asset = props.icon;
      } else {
        asset = <S.Icon icon={props.icon as any} disabled={props.disabled} />;
      }
    }
    if (props.iconPosition == ButtonIconPosition.RIGHT) {
      return (
        <>
          {labelAsset}
          {asset}
        </>
      );
    }
    return (
      <>
        {asset}
        {labelAsset}
      </>
    );
  };

  return (
    <S.Wrapper
      ref={buttonRef}
      className={props.className}
      {...setupTooltipProps({
        tooltip: props.tooltip,
        tooltipPos: props.tooltipPos
      })}
    >
      <S.Container
        minWidth={minWidth}
        disabled={!!props.disabled}
        type={props.type ?? ButtonType.PRIMARY}
        onClick={(event) => {
          event.stopPropagation();
          event.persist();
          action(event);
        }}
      >
        {getContent()}
      </S.Container>
    </S.Wrapper>
  );
};

namespace S {
  const resolveBackground = (p: { type: ButtonType; disabled: boolean; hasError?: boolean } & { theme: Theme }) => {
    if (p.type == ButtonType.PRIMARY) {
      return css`
        background: ${p.disabled ? p.theme.button.primary.backgroundDisabled : p.theme.button.primary.background};

        :hover {
          background: ${!p.disabled
            ? p.theme.button.primary.backgroundHover
            : p.theme.button.primary.backgroundDisabled};
        }
      `;
    }
    if (p.type == ButtonType.SECONDARY) {
      return css`
        background: ${p.theme.button.secondary.background};
        border: solid 1px
          ${p.disabled ? p.theme.button.primary.backgroundDisabled : p.theme.button.secondary.borderColor};

        :hover {
          background: ${p.disabled ? p.theme.button.secondary.background : p.theme.button.primary.backgroundDisabled};
        }
      `;
    }
    if (p.type == ButtonType.TERTIARY) {
      return css`
        background: ${p.theme.button.secondary.background};

        :hover {
          background: ${p.disabled ? p.theme.button.secondary.background : p.theme.button.primary.backgroundDisabled};
        }
      `;
    }
  };

  export const Wrapper = styled.div``;

  export const Container = styled.div<{ type: ButtonType; disabled: boolean; minWidth: number | undefined }>`
    ${(p) => resolveBackground(p)}
    color: ${(p) => p.theme.button.primary.color};
    opacity: ${(p) => (p.disabled ? 0.5 : 1)};
    cursor: ${(p) => (p.disabled ? 'auto' : 'pointer')};
    display: flex;
    align-items: center;
    justify-content: center;
    padding-left: 12px;
    padding-right: 12px;
    height: 36px;
    box-sizing: border-box;
    border-radius: 6px;
    font-size: 16px;
    user-select: none;
    white-space: nowrap;
    column-gap: 8px;
    ${(p) =>
      p.minWidth
        ? css`
            min-width: ${p.minWidth}px;
          `
        : ''}
  `;

  export const LoaderBox = styled.div``;

  export const Label = styled.div`
    display: inline-flex;
  `;

  export const Icon = styled(FontAwesomeIcon)<{ disabled?: boolean }>`
    color: ${(p) => p.theme.forms.icon.color};
    opacity: ${(p) => (p.disabled ? 0.7 : 1)};
  `;
}
